import * as THREE from 'three';
import { SectionConfig, SectionRect, SectionInterface, SectionInsets } from '../types';
import { createSash } from '../geometry/SashGeometry';
import { createGlassPane } from '../geometry/GlassGeometry';
import { createHandle } from '../geometry/HandleGeometry';
import { createRoedenPattern } from '../geometry/RoedenGeometry';
import { MaterialLibrary } from '../materials/MaterialLibrary';
import { PROFILE } from '../utils/profileConstants';

/**
 * TOPHUNG-OUT: Top-hinged outward opening (klepraam / uitzetraam).
 * X-axis rotation at top edge, outward direction.
 */
export class TopHungSection implements SectionInterface {
  readonly group: THREE.Group;
  private pivotGroup: THREE.Group;

  constructor(config: SectionConfig, rect: SectionRect, materialLib: MaterialLibrary, frameMaterial: THREE.Material, insets: SectionInsets) {
    this.group = new THREE.Group();
    this.group.name = `section-tophung-${rect.index}`;
    this.group.userData = { sectionIndex: rect.index, sectionType: 'TOPHUNG-OUT' };

    const effW = rect.width - insets.left - insets.right;
    const effH = rect.height - insets.top - insets.bottom;
    const clearW = effW - 2 * PROFILE.sashWidth;
    const clearH = effH - 2 * PROFILE.sashWidth;

    const sashAssembly = new THREE.Group();

    const sash = createSash(effW, effH, PROFILE.sashWidth, PROFILE.sashDepth, frameMaterial);
    sashAssembly.add(sash);

    const glass = createGlassPane(
      clearW, clearH, PROFILE.glassThickness,
      materialLib.glass(config.infill.tint, config.infill.glazingTier),
    );
    glass.position.set(effW / 2, effH / 2, 0);
    sashAssembly.add(glass);

    if (config.roeden.pattern !== 'none' && config.roeden.mode !== 'none') {
      const roeden = createRoedenPattern(
        clearW, clearH, config.roeden,
        materialLib.roedenMaterial(),
      );
      roeden.position.set(effW / 2, effH / 2, 0);
      sashAssembly.add(roeden);
    }

    // Handle at bottom of effective sash
    const handle = createHandle(materialLib.handleMaterial(config.hardware.handleFinish));
    handle.position.set(effW / 2, PROFILE.sashWidth / 2, PROFILE.sashDepth / 2);
    sashAssembly.add(handle);

    // Pivot at top edge of sash; sashAssembly offset so its top aligns with pivot
    this.pivotGroup = new THREE.Group();
    this.pivotGroup.position.set(0, insets.bottom + effH, 0);
    sashAssembly.position.set(insets.left, -effH, 0);
    this.pivotGroup.add(sashAssembly);
    this.group.add(this.pivotGroup);

    if (config.openAmount > 0) {
      this.setOpenAngle(config.openAmount * (Math.PI / 6));
    }

    this.group.position.set(rect.x, rect.y, 0);
  }

  setOpenAngle(rad: number): void {
    // Outward opening = negative X rotation at top
    this.pivotGroup.rotation.x = -rad;
  }
}
